import api, { Locale, ProjectId } from '@monorepo/api'
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import Spinner from '../components/Spinner'

import {
  DEFAULT_GEO,
  GEO_STORAGE_KEY,
  GEO_RUNTIME_FALLBACKS,
  LANGUAGE_META,
  LANGUAGE_STORAGE_KEY,
  LANGUAGE_TO_GEO,
  type SupportedGeo,
  type GeoRuntimeConfig,
  getGeoRuntimeConfig,
  getResolvedGeoStateForGeo,
  getGeoCopyKey,
  normalizeLanguage,
  type ProjectInfoEntry,
  resolveGeoState,
  type ResolvedGeoState,
  type SupportedLanguage,
} from './config'
import { BaseCdaUrl } from '@monorepo/service'

type GeoRuntimeContextValue = ResolvedGeoState & {
  geoConfig: GeoRuntimeConfig
  geoConfigByGeo: (geo: SupportedGeo) => GeoRuntimeConfig
  languageMeta: (typeof LANGUAGE_META)[SupportedLanguage]
  getCopyKey: (key: string) => string
  syncGeoState: (geo: SupportedGeo) => ResolvedGeoState
  setGeo: (geo: SupportedGeo) => void
}

const DEFAULT_STATE: ResolvedGeoState = {
  geo: DEFAULT_GEO,
  language: GEO_RUNTIME_FALLBACKS[DEFAULT_GEO].defaultLanguage,
  projectId: ProjectId.get(),
  baseCdaUrl: BaseCdaUrl.get(),
  allowedRegions: [DEFAULT_GEO],
  allowedLanguages: [GEO_RUNTIME_FALLBACKS[DEFAULT_GEO].defaultLanguage],
}

const GeoRuntimeContext = createContext<GeoRuntimeContextValue>({
  ...DEFAULT_STATE,
  geoConfig: getGeoRuntimeConfig([], DEFAULT_STATE.geo),
  geoConfigByGeo: (geo) => getGeoRuntimeConfig([], geo),
  languageMeta: LANGUAGE_META[DEFAULT_STATE.language],
  getCopyKey: (key) => getGeoCopyKey(DEFAULT_STATE.geo, DEFAULT_STATE.language, key),
  syncGeoState: () => DEFAULT_STATE,
  setGeo: () => undefined,
})

export const persistState = ({ geo, language, projectId, baseCdaUrl }: ResolvedGeoState) => {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(GEO_STORAGE_KEY, geo)
  window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
  window.document.documentElement.dataset.geo = geo.toLowerCase()
  window.document.documentElement.lang = language
  Locale.set(language)
  if (projectId) ProjectId.set(projectId)
  if (baseCdaUrl) BaseCdaUrl.set(baseCdaUrl)
}

// HACKS. replace with REACT!
const syncLanguageSelection = (language: SupportedLanguage) => {
  if (typeof window === 'undefined') return

  window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language)

  const languageItem = document.querySelector(
    `#languageSwitcher .dropdown-list li img[alt="${language}"]`,
  )

  if (languageItem instanceof HTMLElement) {
    languageItem.closest('li')?.dispatchEvent(new MouseEvent('click', { bubbles: true }))
    return
  }

  window.dispatchEvent(new CustomEvent('languageChanged', { detail: language }))
  window.updateContent?.()
}

const applySubmitGeoState = (nextState: ResolvedGeoState) => {
  Locale.set(nextState.language)
  if (nextState.projectId) ProjectId.set(nextState.projectId)
  if (nextState.baseCdaUrl) BaseCdaUrl.set(nextState.baseCdaUrl)
}

export const GeoRuntimeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [projectInfo, setProjectInfo] = useState<ProjectInfoEntry[]>([])
  const [state, setState] = useState<ResolvedGeoState>(() => DEFAULT_STATE)
  const [isReady, setIsReady] = useState(false)

  const applyResolvedGeoState = useCallback(
    (prevLanguage: SupportedLanguage, nextState: ResolvedGeoState) => {
      if (nextState.language !== prevLanguage) {
        queueMicrotask(() => syncLanguageSelection(nextState.language))
      } else {
        queueMicrotask(() => window.updateContent?.())
      }

      persistState(nextState)
    },
    [],
  )

  useEffect(() => {
    if (!isReady) return
    console.group('Geo state changed')
    console.log('Brand Geo:', state.geo)
    console.log('projectId:', state.projectId)
    console.log('Cda Url:', state.baseCdaUrl)
    console.log({ state })
    console.groupEnd()
  }, [state, isReady])

  useEffect(() => {
    const APP_PROJECT_INFO_URL =
      (typeof window !== 'undefined' && window.env.APP_PROJECT_INFO_URL) || ''
    const projectInfoRequest = APP_PROJECT_INFO_URL
      ? fetch(APP_PROJECT_INFO_URL, { method: 'GET' }).then(
          (response) => response.json() as Promise<ProjectInfoEntry[]>,
        )
      : Promise.resolve([] as ProjectInfoEntry[])

    Promise.allSettled([
      projectInfoRequest,
      api.cda.publicCdaV2IpInfoRetrieve({ cache: 'no-store' }),
    ])
      .then(([projectInfoResult, ipInfoResult]) => {
        if (projectInfoResult.status !== 'fulfilled') throw projectInfoResult.reason

        const projectInfo = projectInfoResult.value
        const ipAlpha2 =
          ipInfoResult.status === 'fulfilled' ? ipInfoResult.value.data.alpha_2 : undefined

        setProjectInfo(projectInfo)
        const state = resolveGeoState({
          search: window.location.search,
          storedGeo: window.localStorage.getItem(GEO_STORAGE_KEY),
          storedLanguage: window.localStorage.getItem(LANGUAGE_STORAGE_KEY),
          projectInfo,
          ipAlpha2,
        })

        console.log({
          state,
        })

        setState(state)
        persistState(state)
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        setIsReady(true)
      })
  }, [])

  useEffect(() => {
    const handleLanguageChanged = (event: Event) => {
      const customEvent = event as CustomEvent<string>
      const nextLanguage = normalizeLanguage(customEvent.detail)

      if (!nextLanguage) return

      setState((prev) => {
        if (!prev.allowedLanguages.includes(nextLanguage)) {
          return prev
        }

        const nextGeoFromLanguage = LANGUAGE_TO_GEO[nextLanguage]
        const geo =
          nextGeoFromLanguage && prev.allowedRegions.includes(nextGeoFromLanguage)
            ? nextGeoFromLanguage
            : prev.geo
        const state = getResolvedGeoStateForGeo({
          geo,
          language: nextLanguage,
          projectInfo,
          allowedRegions: prev.allowedRegions,
          allowedLanguages: prev.allowedLanguages,
        })
        persistState(state)

        return state
      })
    }

    window.addEventListener('languageChanged', handleLanguageChanged)

    return () => window.removeEventListener('languageChanged', handleLanguageChanged)
  }, [projectInfo])

  const getCopyKey = useCallback(
    (key: string) => getGeoCopyKey(state.geo, state.language, key),
    [state.geo, state.language],
  )

  const syncGeoState = useCallback(
    (geo: SupportedGeo) => {
      const currentLanguage = normalizeLanguage(Locale.get()) ?? state.language
      const nextState = getResolvedGeoStateForGeo({
        geo,
        language: currentLanguage,
        projectInfo,
        allowedRegions: state.allowedRegions,
        allowedLanguages: state.allowedLanguages,
      })

      console.log({ nextState })

      applySubmitGeoState(nextState)

      return nextState
    },
    [projectInfo, state],
  )

  const setGeo = useCallback(
    (geo: SupportedGeo) => {
      setState((prev) => {
        const nextState = getResolvedGeoStateForGeo({
          geo,
          language: prev.language,
          projectInfo,
          allowedRegions: prev.allowedRegions,
          allowedLanguages: prev.allowedLanguages,
        })

        applyResolvedGeoState(prev.language, nextState)

        return nextState
      })
    },
    [applyResolvedGeoState, projectInfo, setState],
  )

  const value = useMemo<GeoRuntimeContextValue>(
    () => ({
      ...state,
      geoConfig: getGeoRuntimeConfig(projectInfo, state.geo),
      geoConfigByGeo: (geo) => getGeoRuntimeConfig(projectInfo, geo),
      languageMeta: LANGUAGE_META[state.language],
      getCopyKey,
      syncGeoState,
      setGeo,
    }),
    [getCopyKey, projectInfo, setGeo, state, syncGeoState],
  )

  return (
    <GeoRuntimeContext.Provider value={value}>
      {isReady ? children : <Spinner />}
    </GeoRuntimeContext.Provider>
  )
}

export { GeoRuntimeContext }
